import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Save, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SiteContentEditor({ initialContent, onSaved }) {
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/site-content`, content, { withCredentials: true });
      toast.success('Site content saved');
      onSaved && onSaved(content);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    const c = newCategory.trim();
    if (!c) return;
    if (content.categories.includes(c)) {
      toast.error('Category already exists');
      return;
    }
    setContent({ ...content, categories: [...content.categories, c] });
    setNewCategory('');
  };

  const removeCategory = (cat) => {
    setContent({ ...content, categories: content.categories.filter(c => c !== cat) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-bold text-black font-sans">Site Content</CardTitle>
        <p className="text-sm text-black/60 font-sans">Edit landing page copy, stats, and skill categories.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-6">
          {/* Hero */}
          <div className="space-y-3">
            <h3 className="font-bold text-black font-sans">Hero Section</h3>
            <div>
              <Label htmlFor="hero_headline" className="font-sans font-semibold">Headline</Label>
              <Input id="hero_headline" value={content.hero_headline}
                onChange={(e) => setContent({...content, hero_headline: e.target.value})}
                data-testid="content-hero-headline"
              />
              <p className="text-xs text-black/50 font-sans mt-1">Tip: end the first sentence with a "." — the part after it gets the yellow highlight.</p>
            </div>
            <div>
              <Label htmlFor="hero_subheadline" className="font-sans font-semibold">Sub-headline</Label>
              <Textarea id="hero_subheadline" value={content.hero_subheadline} rows={3}
                onChange={(e) => setContent({...content, hero_subheadline: e.target.value})}
                data-testid="content-hero-subheadline"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="hero_cta_primary" className="font-sans font-semibold">Primary button</Label>
                <Input id="hero_cta_primary" value={content.hero_cta_primary}
                  onChange={(e) => setContent({...content, hero_cta_primary: e.target.value})}
                  data-testid="content-cta-primary"
                />
              </div>
              <div>
                <Label htmlFor="hero_cta_secondary" className="font-sans font-semibold">Secondary button</Label>
                <Input id="hero_cta_secondary" value={content.hero_cta_secondary}
                  onChange={(e) => setContent({...content, hero_cta_secondary: e.target.value})}
                  data-testid="content-cta-secondary"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-3">
            <h3 className="font-bold text-black font-sans">Landing Page Stats</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="space-y-2">
                  <div>
                    <Label className="font-sans font-semibold text-xs">Value {i}</Label>
                    <Input value={content[`stat${i}_value`]}
                      onChange={(e) => setContent({...content, [`stat${i}_value`]: e.target.value})}
                      data-testid={`content-stat${i}-value`}
                    />
                  </div>
                  <div>
                    <Label className="font-sans font-semibold text-xs">Label {i}</Label>
                    <Input value={content[`stat${i}_label`]}
                      onChange={(e) => setContent({...content, [`stat${i}_label`]: e.target.value})}
                      data-testid={`content-stat${i}-label`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div>
            <h3 className="font-bold text-black font-sans mb-2">Footer Text</h3>
            <Input value={content.footer_text}
              onChange={(e) => setContent({...content, footer_text: e.target.value})}
              data-testid="content-footer"
            />
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <h3 className="font-bold text-black font-sans">Skill Categories</h3>
            <p className="text-xs text-black/60 font-sans">Used in the Community Board dropdowns.</p>
            <div className="flex flex-wrap gap-2">
              {content.categories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 rounded-md bg-[#ffd703] border-2 border-black px-2 py-1 text-sm font-sans font-semibold" data-testid={`category-${cat}`}>
                  {cat}
                  <button type="button" onClick={() => removeCategory(cat)} className="hover:bg-black hover:text-white px-1" aria-label={`Remove ${cat}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                placeholder="New category…"
                data-testid="new-category-input"
              />
              <Button type="button" onClick={addCategory} variant="outline" data-testid="add-category-btn">
                <Plus className="w-4 h-4" /> Add
              </Button>
            </div>
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={saving} data-testid="save-content-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Site Content'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
